// this the form to be used when editing or viewing an existing delivery
import React from 'react'
import moment from 'moment'
import {Typography} from '@mui/material'
import {makeStyles} from '@mui/styles'
import Grid from '@mui/material/Grid'
import {Colors, Typography as TypographyStyle} from '../../assets/styles'
import SearchResident from '../SearchResident'
import SelectInput from '../SelectInput'
import Constants from './DeliveryConstants'
import TextInput from '../TextInput'
import MultilineTextInput from '../MultilineTextInput'
import CustomCheckbox from '../CustomCheckbox'
import PropTypes from 'prop-types'
import {useSelector} from 'react-redux'

const NOTES_CHARACTER_LIMIT = 200

const useStyles = makeStyles(theme => ({
  heading: {
    ...TypographyStyle.H3Header,
    lineHeight: '22px',
    marginBottom: '18px',
    color: '#431A1A',
  },
  priorityGrid: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    textAlign: 'center',
  },
  priorityHeading: {
    ...TypographyStyle.BodyHeader,
    textAlign: 'left',
  },
  priorityHeadingDisabled: {
    ...TypographyStyle.BodyHeader,
    color: Colors.darkGrey,
    textAlign: 'left',
  },
  loggedBy: {
    ...TypographyStyle.BodyHeader,
    color: Colors.rxrBlackColor,
    textAlign: 'left',
  },
}))

export default function IncomingDeliveriesForm(props) {
  const classes = useStyles()
  const staffLookup = useSelector(state => state.Staff.staffLookup)

  const isCreatingNewDelivery = !props.id
  const isEditable = !props.receivedAt

  const rowsize = isCreatingNewDelivery ? 6 : 4

  const onChangeResident = value => {
    if (value) {
      props.updateForm({
        residentId: value.id,
        unitId: value.occupancy.unit.id,
      })
    } else {
      props.updateForm({
        residentId: null,
        unitId: null,
      })
    }
  }

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <SearchResident
          disabled={!isCreatingNewDelivery}
          value={props.residentId}
          isRequired={true}
          onSelectResident={onChangeResident}
          error={props.invalidItems.includes('residentId') || props.invalidItems.includes('unitId')}
        />
      </Grid>
      <Grid item md={6} xs={12}>
        <SelectInput
          onChange={val =>
            props.updateForm({
              carrier: val,
            })
          }
          label={'Carrier'}
          isRequired={true}
          options={Constants.CARRIER_OPTIONS}
          value={props.carrier}
          disabled={!isEditable}
          error={props.invalidItems.includes('carrier')}
        />
      </Grid>
      <Grid item md={6} xs={12}>
        <SelectInput
          onChange={val =>
            props.updateForm({
              packageType: val,
            })
          }
          label={'Package type'}
          isRequired={true}
          options={Constants.PACKAGE_OPTIONS}
          value={props.packageType}
          disabled={!isCreatingNewDelivery}
          error={props.invalidItems.includes('packageType')}
        />
      </Grid>
      <Grid item xs={12}>
        <TextInput
          label="Tracking number"
          placeholder=""
          maxLength={120}
          onChange={val => props.updateForm({trackingNumber: val})}
          value={props.trackingNumber}
          isDisabled={!isEditable}
        />
      </Grid>

      <Grid item md={6} xs={12}>
        <TextInput
          label="Arrived on"
          placeholder=""
          isDisabled
          value={moment(props.createdAt).format(Constants.PACKAGE_TABLE_DATETIME_FORMAT)}
        />
      </Grid>
      {!isEditable && (
        <Grid item md={6} xs={12}>
          <TextInput
            label={
              props.deliveredTo === 'unit'
                ? 'Marked Delivered to unit by'
                : props.deliveredTo === 'person'
                ? 'Picked up at front desk by'
                : 'Delivered on'
            }
            placeholder=""
            isDisabled
            // I think we need to somehow work `deliveredStaffId` into this if it was delivered to Unit
            value={`${props.recipientName ? props.recipientName + ', ' : ''} ${
              props.receivedAt ? new moment(props.receivedAt).format(Constants.PACKAGE_TABLE_DATETIME_FORMAT) : ''
            }`}
          />
        </Grid>
      )}

      {props.staffId && staffLookup[props.staffId] ? (
        <Grid item xs={12} className={classes.priorityGrid}>
          <Typography className={classes.loggedBy}>Logged by: {staffLookup[props.staffId].displayName}</Typography>
        </Grid>
      ) : null}

      <Grid item xs={12}>
        <MultilineTextInput
          label="Additional details"
          placeholder=""
          helperText={`The resident will see these notes. ${NOTES_CHARACTER_LIMIT} character limit.`}
          rows={rowsize}
          rowsMax={rowsize}
          maxLength={NOTES_CHARACTER_LIMIT}
          onChange={val => props.updateForm({notes: val})}
          value={props.notes}
          isDisabled={!isEditable}
        />
      </Grid>
      <Grid item xs={12} className={classes.priorityGrid}>
        <CustomCheckbox
          isChecked={props.priority === 'HIGH'}
          onChange={checked =>
            props.updateForm({
              priority: checked ? 'HIGH' : 'LOW',
            })
          }
          label={'High priority?'}
          isDisabled={!isEditable}
        />
      </Grid>
    </Grid>
  )
}

IncomingDeliveriesForm.propTypes = {
  updateForm: PropTypes.func.isRequired,
  invalidItems: PropTypes.array,

  // these are form fields
  priority: PropTypes.string,
  unitId: PropTypes.string,
  residentId: PropTypes.string,
  carrier: PropTypes.string,
  packageType: PropTypes.string,
  trackingNumber: PropTypes.string,
  notes: PropTypes.string,
  createdAt: PropTypes.string,
  receivedAt: PropTypes.string,
  staffId: PropTypes.string,

  // deliveredDetails
  deliveredTo: PropTypes.string,
  recipientName: PropTypes.string,
  deliveredStaffId: PropTypes.string,
}
